/**
 * Utilities for retrieving TunerVar data from state
 *
 * @module lib/configuration/TunerVarUtils
 */
import { Map } from 'immutable';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import { createSelector } from 'reselect';

import * as Constants from '../../constants';
import { API_PATH } from '../../constants/ApiActions';
import { CountryCodes } from '../../constants/CountryCodes';
import { TunerVarStateKeys } from '../../constants/TunerVarStateKeys';
import { type ApiEndpoint, type IMapOf, type State, type ValueOf } from '../../types';
import { type TunerVarData } from '../../types/globals/webpack';

// Get TunerVars that have been rendered onto the page
const rawTunerVarsData = window.__CONFIG__;
const emptyMap = Map();

/**
 * Expose raw config for components use
 * before store is available
 *
 * @returns {object}
 */
export function getRawTunerVarsData(): TunerVarData {
  return rawTunerVarsData;
}

// returns {ImmutableMap} of all tuner vars
// Probably do not use directly - Does not respect overrides
export function getTunerVars(state: State): IMapOf<TunerVarData> {
  return state?.tunerVars ?? (Map(getRawTunerVarsData()) as IMapOf<TunerVarData>);
}

// returns {ImmutableMap} of all tuner var overrides
export function getTunerVarOverrides(state: State): IMapOf<TunerVarData> {
  if (state && state.debug) {
    return state.debug.getIn(['flagOverrides', 'tunerVars'], emptyMap);
  }
  return emptyMap as IMapOf<TunerVarData>;
}

/**
 * Convert a string into an integer.  If the string is null or empty or not parseable, it will return NaN.
 *
 * @param {string} str - input string
 * @returns {number | NaN} - parsed number or NaN
 */
export function stringToInt(str: string): number | typeof NaN {
  if (isNumber(str)) {
    return str;
  }

  if (isString(str) && str.length > 0) {
    return parseInt(str, 10);
  }
  return NaN;
}

// returns {ImmutableMap} of tuner vars
// respects overrides
export const getOverriddenTunerVarMap = createSelector(
  getTunerVars,
  getTunerVarOverrides,
  (tunerVars, overrides) => {
    if (!tunerVars) return emptyMap;
    return tunerVars.mergeDeepWith((_oldVal, newVal) => {
      if (newVal.constructor === [].constructor && newVal.length === 2) {
        return newVal[1]; // handle list merging
      }
      return newVal;
    }, overrides);
  },
);

// build a selector for a particular tuner var
// first argument should be a tuner var key, second is an optional fallback value
// returns selector function to get the value of `tunerVar`
function tunerVarFactory<TunerVar extends ValueOf<typeof TunerVarStateKeys>>(
  tunerVar: TunerVar,
  fallback?: $TSFixMe,
): TunerVarData[TunerVar] {
  return createSelector(getOverriddenTunerVarMap, (overriddenTunerVars): TunerVarData[TunerVar] => {
    return (overriddenTunerVars as IMapOf<TunerVarData>).get(tunerVar, fallback);
  });
}

export const userProfile2Enabled = tunerVarFactory(TunerVarStateKeys.ENABLE_USER_PROFILE_2, false);
export const getOptOutAllowed = tunerVarFactory(TunerVarStateKeys.WEB_2016_CLIENT_OPT_OUT_ALLOWED);
export const getForceFlash = tunerVarFactory(TunerVarStateKeys.FORCE_FLASH);
export const getShouldAlertOnClientMismatch = tunerVarFactory(
  TunerVarStateKeys.WEB_ALERT_ON_CLIENT_VERSION_MISMATCH,
);
export const isMegastarModesEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_MEGASTAR_MODES,
  false,
);
export const isAutomaticPlaylistEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_AUTOMATIC_PLAYLIST_UNLOCK,
  false,
);
export const isPaContentIdTargetingEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_PA_CONTENT_ID_TARGETING,
  false,
);
export const isCCPALinkEnabled = tunerVarFactory(TunerVarStateKeys.WEB_CCPA_LINK_ENABLED, false);
export const getIsPrivacyLawComplianceEnabled = tunerVarFactory(
  TunerVarStateKeys.PRIVACY_LAW_COMPLIANCE_ENABLED,
  false,
);
export const getCCPALinkUrl = tunerVarFactory(TunerVarStateKeys.WEB_CCPA_LINK_URL, '');
export const isOnDemandSpecialOffersEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_ON_DEMAND_SPECIAL_OFFERS,
  false,
);
export const isOnDemandFamilyPlanAcceptInviteEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_ON_DEMAND_FAMILY_PLAN_ACCEPT_INVITE,
  false,
);
export const getIsBlackhawkEnabled = tunerVarFactory(TunerVarStateKeys.ENABLE_BLACKHAWK, false);
export const getIsBotDetectionEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_BOT_DETECTION,
  false,
);
export const getIsNonBinaryGenderEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_NON_BINARY_GENDER,
  false,
);
export const isDesktopDeepLinkingEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_DESKTOP_DEEP_LINKING,
  false,
);
export const isArtistBackstageUrqlEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_ARTIST_BACKSTAGE_URQL,
  false,
);
export const logOutOfAllDevicesEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_LOG_OUT_OF_ALL_DEVICES,
  false,
);
export const deviceUuidEnabled = tunerVarFactory(TunerVarStateKeys.ENABLE_DEVICE_UUID, false);
export const collectionV7EndpointsEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_COLLECTION_V7_ENDPOINTS,
  false,
);
export const curatorExperienceEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_CURATOR_EXPERIENCE,
  false,
);
export const curatorDeepLinkingEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_CURATOR_DEEP_LINKING,
  false,
);
export const sentryLoggingEnabled = tunerVarFactory(TunerVarStateKeys.ENABLE_SENTRY_LOGGING, false);
export const premiumIncreaseEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_PREMIUM_INCREASE,
  false,
);
export const audioPlayerSDKEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_AUDIO_PLAYER_SDK,
  false,
);
export const graphQLMigrationEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_GRAPHQL_MIGRATION,
  false,
);
export const allowT1UsersForApsStation = tunerVarFactory(
  TunerVarStateKeys.ALLOW_T1_USERS_APS_STATION,
  false,
);
export const isUID2Enabled = tunerVarFactory(TunerVarStateKeys.IS_UID2_ENABLED, false);

export const clearRewardsOnPASessionEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_CLEAR_REWARDS_ON_PA_SESSION,
  false,
);

export const podcastTranscriptsEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_PODCAST_TRANSCRIPTS,
  false,
);

export const getAPSTestMode = tunerVarFactory(TunerVarStateKeys.APS_TEST_MODE);
export const getTestMode = tunerVarFactory(TunerVarStateKeys.CHRONOS_TEST_MODE);
export const getCustomerServiceAuthToken = tunerVarFactory(TunerVarStateKeys.CS_AUTH_TOKEN);
export const getReplayableTrackHistoryCount = tunerVarFactory(
  TunerVarStateKeys.REPLAYABLE_TRACK_HISTORY_COUNT,
);
export const getVideoProgressMaxBufferSecs = tunerVarFactory(
  TunerVarStateKeys.VALUE_EXCHANGE_VIDEO_PROGRESS_MAX_BUFFERING_SECS,
);

export const getTestVideoAdId = tunerVarFactory(TunerVarStateKeys.TEST_VIDEO_AD_ID);
export const getTestAudioAdId = tunerVarFactory(TunerVarStateKeys.FORCED_AUDIO_AD_ID);
export const getForcedDisplayAdId = tunerVarFactory(TunerVarStateKeys.FORCED_DISPLAY_AD_ID);
export const getAudibilityTestModeEnabled = tunerVarFactory(TunerVarStateKeys.AUDIBILITY_TEST_MODE);
export const getLineId = tunerVarFactory(TunerVarStateKeys.LINE_ID);
export const getCreativeId = tunerVarFactory(TunerVarStateKeys.CREATIVE_ID);

export const getFacebookShareAppId = tunerVarFactory(TunerVarStateKeys.FACEBOOK_SHARE_APP_ID);
export const getFacebookConnectPermissions = tunerVarFactory(
  TunerVarStateKeys.FACEBOOK_CONNECT_PERMISSIONS,
);

export const getTwitterAppKey = tunerVarFactory(TunerVarStateKeys.TWITTER_SHARE_CONSUMER_KEY);
export const getTwitterAppSecret = tunerVarFactory(TunerVarStateKeys.TWITTER_SHARE_CONSUMER_SECRET);

export const getHost = tunerVarFactory(TunerVarStateKeys.APPLICATION_HOSTNAME);
export const getPort = tunerVarFactory(TunerVarStateKeys.APPLICATION_PORT);

export const getAudioPlayerSupportedBrowsers = tunerVarFactory(
  TunerVarStateKeys.AUDIO_PLAYER_SUPPORTED_BROWSERS,
);
export const getSupportedBrowsers = tunerVarFactory(TunerVarStateKeys.SUPPORTED_BROWSERS);

export const getStaticImageUrl = tunerVarFactory(TunerVarStateKeys.STATIC_IMAGE_URL, '');
export const getDynamicImageUrl = tunerVarFactory(TunerVarStateKeys.DYNAMIC_IMAGE_URL, '');
export const getDynamicImageSizes = tunerVarFactory(TunerVarStateKeys.DYNAMIC_IMAGE_SIZES, '');

export const getStatsJsonApiUrl = tunerVarFactory(TunerVarStateKeys.STATS_JSON_API_URL);
export const getStatsBatchSize = tunerVarFactory(TunerVarStateKeys.STATS_COLLECTION_BATCH_SIZE);
export const getStatsInterval = tunerVarFactory(TunerVarStateKeys.STATS_COLLECTION_INTERVAL_MS);
export const isStatsTestLoggerEnabled = tunerVarFactory(TunerVarStateKeys.ENABLE_STATS_TEST_LOGGER);

export const getPostalCodeValidationRegExp = tunerVarFactory(
  TunerVarStateKeys.POSTAL_CODE_VALIDATION_REGEXP,
);
export const getCountryCode = tunerVarFactory(TunerVarStateKeys.COUNTRY_CODE);

export const getPollCollectionInterval = tunerVarFactory(
  TunerVarStateKeys.POLL_COLLECTION_INTERVAL,
);

export const getDisableThirdPartyTrackers = tunerVarFactory(
  TunerVarStateKeys.DISABLE_THIRD_PARTY_BEACONS,
);

export const getWebPandoraIdsWithDoubleSlashUri = tunerVarFactory(
  TunerVarStateKeys.WEB_PANDORA_IDS_WITH_DOUBLE_SLASH_URI,
);
export const getDeepLinkingUrl = tunerVarFactory(TunerVarStateKeys.DEEP_LINKING_URL, '');

export const getPremiumAccessStartToneUrl = tunerVarFactory(
  TunerVarStateKeys.PREMIUM_ACCESS_START_TONE_URL,
);
export const getPremiumAccessEndToneUrl = tunerVarFactory(
  TunerVarStateKeys.PREMIUM_ACCESS_END_TONE_URL,
);
export const getPremiumAccessWelcomeMessageUrl = tunerVarFactory(
  TunerVarStateKeys.PREMIUM_ACCESS_WELCOME_MESSAGE_URL,
);
export const getPremiumAccessWelcomeMessageLimit = tunerVarFactory(
  TunerVarStateKeys.PREMIUM_ACCESS_WELCOME_MESSAGE_LIMIT,
);

export const getMusicTimeoutForAdBlockHandler = tunerVarFactory(
  TunerVarStateKeys.WEB_ANTI_AD_BLOCKER_MUSIC_TIMEOUT,
);
export const getAdBlockCheckInterval = tunerVarFactory(
  TunerVarStateKeys.WEB_AD_BLOCKER_CHECK_INTERVAL,
);

export const getIsResponsiveSubscriptionEnabled = tunerVarFactory(
  TunerVarStateKeys.ENABLE_RESPONSIVE_SUBSCRIPTION,
  false,
);

export const isUnitedStates = createSelector(
  getCountryCode,
  countryCode => countryCode === CountryCodes.US,
);

export const getApplicationHost = createSelector(
  getPort,
  getHost,
  (port: string, host: string) => `https://${host}${port ? ':' : ''}${port}`,
);

export const getApplicationHostShare = createSelector(
  getPort,
  getHost,
  (port: string, host: string) => {
    const portNum = stringToInt(port);
    return `https://${host}${port && !(portNum === 443 || portNum === 80) ? `:${port}` : ''}`;
  },
);

export const getPaidHost = createSelector(
  tunerVarFactory(TunerVarStateKeys.PAID_HOSTNAME),
  host => host && `https://${host}`,
);

export const getVideoStartTimeoutMs = createSelector(
  tunerVarFactory(TunerVarStateKeys.VIDEO_START_TIMEOUT_MS),
  timeout => timeout || Constants.Ads.Video.PlaybackConstants.DEFAULT_VIDEO_START_TIMEOUT_MS,
);

export const getAdRefreshValue = createSelector(
  tunerVarFactory(TunerVarStateKeys.AD_REFRESH_INTERVAL),
  (timeInSeconds: number) => timeInSeconds * 1000,
);

export const getOnDemandArtistMessageData = createSelector(
  tunerVarFactory(TunerVarStateKeys.ON_DEMAND_ARTIST_MESSAGE_DATA),
  (messageData: string) => {
    const data = (messageData || '').split(':');
    return {
      onDemandArtistMessageArtistUidHex: data[0] || null,
      onDemandArtistMessageIdHex: data[1] || null,
    };
  },
);

export const getOnDemandArtistMessageToken = createSelector(
  tunerVarFactory(TunerVarStateKeys.ON_DEMAND_ARTIST_MESSAGE_DATA),
  messageData => {
    return messageData || '';
  },
);

export const hasOnDemandArtistMessageData = createSelector(
  getOnDemandArtistMessageData,
  result => result.onDemandArtistMessageIdHex && result.onDemandArtistMessageArtistUidHex,
);

/**
 * Request for test artist message in playlist fragments
 *
 * @param {object} state - redux state
 * @returns {string} test message. Format is "<index>:<messageId>"
 *    where index is position in fragment and messageId is:
 *      1 : deliveryType AFTER_TRACK
 *      2-5 : deliveryType ANYTIME (Checkout RadioArtistMessageService::getTestMessage for additional characteristics)
 *      6 : deliveryType BEFORE_TRACK
 *   so: "1:1:2:1" means the getFragment (assuming 4 tracks per fragment) will be "Track,Test AAM 1,Track,Test AAM 1,Track,Track"
 */
export const getTestArtistMessage = createSelector(
  tunerVarFactory(TunerVarStateKeys.TEST_ARTIST_MESSAGE),
  testMessage => testMessage || null,
);

/*
 * Analog of getTestArtistMessage for VoiceTracks
 */
export const getTestVoiceTrack = createSelector(
  tunerVarFactory(TunerVarStateKeys.TEST_VOICE_TRACK),
  testMessage => testMessage || null,
);

export function getPaidHostApiPath(state: State, endpoint: ApiEndpoint): string {
  return getPaidHost(state) + API_PATH + `/${endpoint}`;
}

export function getAmpUrl(state: State, name: string): string {
  const ampUrls = tunerVarFactory(TunerVarStateKeys.AMP_URLS, {})(state);
  return ampUrls.get(name) || '';
}

export const getSheerIdClientApiBaseUrl = tunerVarFactory(
  TunerVarStateKeys.SHEER_ID_CLIENT_API_BASE_URL,
);

export const getTunerVarAnalyticsFlags = (state: State): Array<object> | string => {
  // Flags must be unique single characters for each included tunerVar
  return [
    {
      flag: 'S',
      selector: isOnDemandSpecialOffersEnabled,
    },
    {
      flag: 'F',
      selector: isOnDemandFamilyPlanAcceptInviteEnabled,
    },
  ].reduce((acc, current) => {
    return current.selector(state) ? acc + current.flag : acc;
  }, '');
};
